/* eslint-disable react/jsx-max-depth */
import { useMemo } from "react";

import { useTranslate, type Translate } from "@similarweb/i18n-provider";
import type { AdIntelligenceRecordsAdPlacementsResponse } from "@similarweb/pro-api-client";
import { ShareBarCell, Table } from "@similarweb/pro-logic-components";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Card, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { percentageSignFilter } from "app/filters/ngFilters";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { AdIntelligenceController } from "../../shared/types/types";
import { BottomBorderHider } from "../../StyledComponents";
import { NA_VALUE } from "../../utils/utils";
import { useAdPlacements } from "../hooks/useAdPlacements";
import { getEmptyState } from "../PerformanceWidget";
import { CompareTableWrapper, StyledBanner } from "../StyledComponents";
import { adPlacementConfig, fillMissingPlacementKeys, normalizePercentages } from "../utils/graphUtils";
import { calculateCompareEmptyStateHeight } from "../utils/utils";

const columnConfig = (translate: Translate): ColumnsType => [
    {
        title: () => translate("ad.intelligence.display.advertiser.ad.placements.advertiser.title"),
        dataIndex: "advertiser",
        width: 160,
        render: (advertiser: string) => {
            const getWebsiteLink = (key: string) => {
                return displayAdvertisingNavigation.overview.getLink({ ...proNavigator.getParams(), key });
            };
            return (
                <CoreWebsiteCell
                    domain={advertiser}
                    icon={getFaviconUrl(advertiser)}
                    textAndIconMargin="8px"
                    showToolTip
                    internalLink={getWebsiteLink(advertiser)}
                    spacingDomainAndAddons={false}
                    TooltipComponent={({ children }) => (
                        <Tooltip
                            content={translate(
                                "ad.intelligence.display.advertiser.ad.placements.advertiser.cell.tooltip",
                                {
                                    site: advertiser,
                                },
                            )}
                            placement="top"
                        >
                            {children}
                        </Tooltip>
                    )}
                />
            );
        },
    },
    {
        get title() {
            return translate("ad.intelligence.display.advertiser.ad.placements.placement.title");
        },
        dataIndex: "placementPercentages",
        render: (placement: AdIntelligenceRecordsAdPlacementsResponse["data"][number]["placementPercentages"]) => {
            const filledPlacement = fillMissingPlacementKeys(placement);
            const rawValues = adPlacementConfig.map((config) => filledPlacement[config.id]);
            const normalizedValues = normalizePercentages(rawValues);

            return normalizedValues.every((value) => !value) ? (
                <Flex justifyContent="center">{NA_VALUE}</Flex>
            ) : (
                <ShareBarCell
                    values={normalizedValues}
                    colorSet={adPlacementConfig.map((config) => config.color)}
                    tooltip={{
                        get title() {
                            return translate(
                                "ad.intelligence.display.advertiser.ad.placements.placement.tooltip.title",
                            );
                        },
                        labels: adPlacementConfig.map((config) => translate(config.translationKey)),
                        valueFormatter: (value: number) =>
                            value ? (percentageSignFilter()(value, 1, NA_VALUE) as string) : NA_VALUE,
                    }}
                    labelThreshold={0.13}
                />
            );
        },
    },
];

interface IAdPlacementsCompare {
    controller?: AdIntelligenceController;
}

export const AdPlacementsCompare = ({ controller = "advertiser" }: IAdPlacementsCompare) => {
    const subtitleFilters = useSubtitleFilters("date");
    const { data, isPending, error } = useAdPlacements(controller);
    const { translate } = useTranslate();

    const tableColumns = useMemo(() => columnConfig(translate), [translate]);

    const dataSorted = useMemo(() => {
        const hasPlacementData =
            data?.data && data.data.some((item) => Object.keys(item.placementPercentages).length > 0);

        if (!hasPlacementData) {
            return [];
        }

        const orderedDomains = chosenSites.get();
        return [...data.data].sort((a, b) => {
            const indexA = orderedDomains.indexOf(a.advertiser);
            const indexB = orderedDomains.indexOf(b.advertiser);
            return indexA - indexB;
        });
    }, [data?.data]);

    const competitorCount = chosenSites.get().length || 0;
    const calculatedHeight = calculateCompareEmptyStateHeight(competitorCount);

    const renderEmptyState = useMemo(
        () =>
            getEmptyState(
                !!error,
                true,
                calculatedHeight,
                "ad.intelligence.overview.widget.empty.state.short.subtitle",
            ),
        [error, calculatedHeight],
    );

    return (
        <Card
            background={colorsPalettes.carbon[0]}
            margin="0 0 24px"
            maxHeight="377px"
            overflow="hidden"
            position="relative"
        >
            <CompareTableWrapper>
                <Table
                    data={dataSorted}
                    columnConfig={tableColumns}
                    theme="white"
                    bordersConfig={{ body: false, header: false }}
                    isLoading={isPending}
                    loaderRowCount={competitorCount}
                    noData={{ Component: () => renderEmptyState }}
                >
                    <Table.TableTop withoutRowBorders>
                        <Table.TableTop.Title
                            title={translate(`ad.intelligence.overview.ad.placement.widget.title`)}
                            tooltip={translate(`ad.intelligence.overview.ad.placement.widget.title.tooltip`)}
                            filters={subtitleFilters}
                        />
                        {isPending ? (
                            <span />
                        ) : (
                            <StyledBanner
                                alignItems="center"
                                $isCompareMode
                            >
                                {translate("ad.intelligence.overview.ad.placement.widget.banner.title")}
                            </StyledBanner>
                        )}
                    </Table.TableTop>
                </Table>
            </CompareTableWrapper>
            <BottomBorderHider />
        </Card>
    );
};
